import { Button } from '@mui/material';
import { PaymentRow, PaymentRowProps, REQUIRED_FIELDS } from 'components/PaymentRow';
import { Popup } from 'components/Popup';
import { Rates } from 'components/Rates';
import { PaymentRowData, usePaymentDraft } from 'hooks/payment';
import { Controller, Validate, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

type PaymentFormData = {
  payment: (PaymentRowData | undefined)[];
};

type ValidateHandler =
  | Validate<PaymentRowData | undefined>
  | Record<string, Validate<PaymentRowData | undefined>>
  | undefined;

export const CreatePayment = () => {
  const navigate = useNavigate();
  const { updateDraft, draft } = usePaymentDraft();

  const { control, handleSubmit, setError, getValues, reset, watch } = useForm<PaymentFormData>({
    defaultValues: { payment: draft },
  });

  watch(({ payment }) => {
    if (!payment) {
      return;
    }

    const paymentsToProceed: PaymentRowData[] = payment.filter((payment): payment is PaymentRowData => !!payment);

    updateDraft(paymentsToProceed);
  });

  const { fields, remove, append, update } = useFieldArray({ name: 'payment', control, keyName: 'id' });
  const paymentValues = useWatch({ name: 'payment', control: control, defaultValue: [] });
  const lookupIndex = (id: number) => getValues().payment.findIndex((field) => field?.id === id);

  const onRemove = (id: number) => {
    const removedIndex = lookupIndex(id);

    remove(removedIndex);
  };

  const convertTable = (text: string, start = 0) => {
    const rows = text.split('\n');

    const payments: PaymentRowData[] = rows
      .filter((row) => !!row)
      .map((row, index) => {
        const [account, value, comment] = row.split('\t');

        return {
          id: index + start,
          cardNumber: account,
          amount: value ? parseInt(value.replaceAll(/[\,\s]*/g, ''), 10) : 0,
          bank: '',
          secondaryAccount: '',
          comment: comment || '',
          backendId: null,
        };
      });

    payments.forEach((payment, index) => {
      update(start + index, payment);
    });
  };

  const updateAmounts = (text: string, start = 0) => {
    const amounts = text.split('\n').map((row) => parseInt(row.replaceAll(' ', ''), 10));

    amounts.forEach((amount, index) => {
      const value = paymentValues[start + index];

      if (!value) {
        return;
      }

      update(start + index, { ...value, amount });
    });
  };

  const onSubmit = (data: PaymentFormData) => {
    const paymentsToProceed: PaymentRowData[] = data.payment.filter((payment): payment is PaymentRowData => !!payment);

    updateDraft(paymentsToProceed);
    navigate('/confirm');
  };

  const onValidateRow: ValidateHandler = (value) => {
    if (!value) {
      return;
    }

    if (!REQUIRED_FIELDS.reduce((res: boolean, fieldName) => !!value[fieldName] || res, false)) {
      return;
    }

    let isValid = true;

    REQUIRED_FIELDS.forEach((field) => {
      if (!value[field]) {
        isValid = false;
      }
    });

    return isValid ? isValid : 'Заполните обязательные поля';
  };

  const onRequiredError: PaymentRowProps['onRequiredError'] = ({ id, field }) => {
    setError(`payment.${lookupIndex(id)}.${field}`, { type: 'required' }, { shouldFocus: true });
  };

  const onValueError: PaymentRowProps['onValueError'] = ({ id, message }) => {
    setError(`payment.${lookupIndex(id)}`, { type: 'validate', message }, { shouldFocus: true });
  };

  return (
    <Popup onClose={() => navigate('/')}>
      <div className="flex justify-between">
        <h1 className="text-3xl font-bold">Новый реестр</h1>
        <Rates />
      </div>
      <div className="flex flex-row items-end">
        <div className="flex-grow mr-4">
          <div className="text-sm my-3">Суммы выплат от 5 000 руб. до ∞</div>
          <div className="text-sm my-3">
            В реестрах, где больше 10 выплат с суммами до 15 000 руб. накладывается дополнительная&nbsp;комиссия в 1%
            (только на данные платежи)
          </div>
        </div>
        <Button
          className="mb-3 w-60 flex-grow-0"
          type="button"
          variant="outlined"
          onClick={() => {
            reset({ payment: [] });

            for (let i = 0; i < 10; i++) {
              append({} as PaymentRowData);
            }
          }}
        >
          Очистить всё
        </Button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          {fields.map((field, index) => (
            <Controller
              key={field.id}
              control={control}
              name={`payment.${index}`}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <PaymentRow
                    key={field.id}
                    error={error}
                    number={index + 1}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    onPaste={(text) => {
                      convertTable(text, index);
                    }}
                    onPasteAmount={(text) => updateAmounts(text, index)}
                    onRemove={onRemove}
                    onRequiredError={onRequiredError}
                    onValueError={onValueError}
                  />
                );
              }}
              rules={{ validate: onValidateRow }}
            />
          ))}
          <Button className="mb-3" type="submit">
            Посчитать и оплатить
          </Button>
          <Button
            className="mb-3"
            type="button"
            variant="outlined"
            onClick={() => {
              for (let i = 0; i < 10; i++) {
                append({} as PaymentRowData);
              }
            }}
          >
            Добавить ещё 10 строк
          </Button>
        </div>
      </form>
    </Popup>
  );
};
